import { Box } from '@mui/material';
import type { ReactNode } from 'react';

const IPAD_LANDSCAPE_MEDIA =
  '@media (min-width: 900px) and (max-width: 1399.95px) and (orientation: landscape)';
const SIDE_INSPECTOR_MEDIA = '@media (min-width: 1400px)';

export type DraftPersistenceStatus = 'ready' | 'saved' | 'unavailable';

type ComposerDocumentHeaderProps = {
  title: string;
  persistenceStatus: DraftPersistenceStatus;
  actions: ReactNode;
};

const ComposerDocumentHeader = ({
  title,
  persistenceStatus,
  actions,
}: ComposerDocumentHeaderProps) => (
  <Box component="header" data-repertoire-create-document-header sx={documentHeaderStyles}>
    <Box component="h1" data-repertoire-create-document-title sx={documentTitleStyles}>
      {title}
    </Box>

    <Box
      data-repertoire-create-document-actions
      data-repertoire-create-compact-actions
      sx={documentActionsStyles}
    >
      {actions}
    </Box>

    {persistenceStatus === 'unavailable' && (
      <Box data-repertoire-create-recovery-error role="alert" sx={recoveryErrorStyles}>
        Local recovery is unavailable. Save to your library to protect your changes.
      </Box>
    )}

    <Box role="status" aria-live="polite" aria-atomic="true" sx={visuallyHiddenStyles}>
      {persistenceStatus === 'saved' ? 'Changes saved locally.' : ''}
    </Box>
  </Box>
);

const documentHeaderStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto' },
  alignItems: 'center',
  gap: '8px 16px',
  minWidth: 0,
  minHeight: '56px',
  padding: '4px 2px 10px',
  borderBottom: '1px solid rgba(226, 234, 255, 0.12)',
  [IPAD_LANDSCAPE_MEDIA]: {
    minHeight: '54px',
    padding: '2px 2px 8px',
  },
};

const documentTitleStyles = {
  minWidth: 0,
  margin: 0,
  overflow: 'hidden',
  color: '#F3F7FF',
  fontSize: { xs: '20px', md: '22px' },
  fontWeight: 800,
  letterSpacing: '-0.015em',
  lineHeight: 1.2,
  textAlign: 'left',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const documentActionsStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
  flexWrap: 'wrap',
  gap: '8px',
  minWidth: 0,
  '& > *': {
    flexShrink: 0,
  },
  [IPAD_LANDSCAPE_MEDIA]: {
    flexWrap: 'nowrap',
  },
  [SIDE_INSPECTOR_MEDIA]: {
    display: 'none',
  },
};

const recoveryErrorStyles = {
  gridColumn: '1 / -1',
  color: '#FFD5A6',
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: 1.4,
};

const visuallyHiddenStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default ComposerDocumentHeader;
